import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PlusCircle, Store } from "lucide-react";
import { api, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { OrderStatusChip } from "@/components/marketplace/OrderStatusChip";

const NEXT = { recommended: "ordered", ordered: "sample_collected", sample_collected: "result_ready" };
const NEXT_LABEL = { recommended: "Mark ordered", ordered: "Mark sample collected", sample_collected: "Mark result ready" };

export function TestsTab({ patient }) {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const canManage = ["admin", "doctor", "staff"].includes(user.role);
  const load = useCallback(() => api.get(`/marketplace/orders?patient_id=${patient.id}`).then((r) => setOrders(r.data)).catch(() => {}), [patient.id]);
  useEffect(() => { load(); }, [load]);

  const advance = async (o) => { await api.put(`/marketplace/orders/${o.id}/status`, { status: NEXT[o.status] }); load(); };

  return (
    <div className="space-y-4" data-testid="tests-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">Diagnostic tests and devices assigned from marketplace partners.</p>
        {canManage && <Link to="/marketplace" data-testid="assign-from-marketplace" className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal-700 hover:underline"><PlusCircle className="w-4 h-4" /> Assign from marketplace</Link>}
      </div>
      {orders.length === 0 && (
        <div className="clinical-card p-8 text-center" data-testid="no-tests">
          <Store className="w-8 h-8 text-slate-300 mx-auto" />
          <p className="text-sm text-slate-500 mt-2">No tests assigned yet.</p>
        </div>
      )}
      <div className="space-y-3">
        {orders.map((o) => (
          <div key={o.id} className="clinical-card p-5 flex flex-wrap items-center gap-4" data-testid={`test-order-${o.id}`}>
            <div className="flex-1 min-w-[220px]">
              <p className="font-heading font-semibold text-slate-900">{o.product_name}</p>
              <p className="text-xs text-slate-500 mt-0.5">{o.brand_name} · {o.brand_category === "dx" ? "Diagnostics" : "Medtech"}{o.product_price ? ` · ${o.product_price}` : ""}</p>
              {o.note && <p className="text-sm text-slate-600 mt-1.5">“{o.note}”</p>}
              <p className="text-[11px] text-slate-400 mt-1.5">By {o.assigned_by_name} · {fmtDateTime(o.created_at)}</p>
            </div>
            <OrderStatusChip status={o.status} />
            {canManage && NEXT[o.status] && (
              <button data-testid={`advance-order-${o.id}`} onClick={() => advance(o)} className="text-sm font-semibold text-teal-700 hover:underline whitespace-nowrap">{NEXT_LABEL[o.status]} →</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
